/**
 * components/ActiveFilterChips.jsx — v2.87 Phase 3a
 * --------------------------------------------------
 * Above-results active-filter strip. Each non-default filter is rendered
 * as a removable chip; multi-select fields explode into one chip per
 * selected value. A "Clear all" link appears on the right.
 *
 *   [Sale ×]  [Apartment ×]  [House ×]  [2 BHK ×]  [Verified ×]  · · ·  [Clear all]
 *
 * Decoupled from the filter source — accepts `chips: [{key, value, label, onRemove}]`
 * from the parent. Each consumer (listing / project) builds its own chip array.
 */

import React from "react";
import { X } from "lucide-react";

export default function ActiveFilterChips({ chips = [], onClearAll, testidPrefix = "afc" }) {
  if (!chips.length) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-1.5 pb-3"
      data-testid={`${testidPrefix}-root`}
    >
      <span className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mr-1">
        Active:
      </span>
      {chips.map((c, idx) => (
        <button
          key={`${c.key}-${c.value ?? idx}`}
          type="button"
          onClick={c.onRemove}
          data-testid={`${testidPrefix}-chip-${c.key}${c.value != null ? `-${String(c.value).replace(/[\s/]+/g, "-")}` : ""}`}
          className="group inline-flex items-center gap-1 h-7 pl-2.5 pr-1.5 rounded-full
                     bg-[#EFF6FF] text-[#0044CC] border border-[#BFDBFE]
                     text-[11.5px] font-semibold transition-all
                     hover:bg-[#DBEAFE] hover:border-[#0055FF] hover:text-[#003D99]"
          aria-label={`Remove filter ${c.label}`}
        >
          <span className="max-w-[180px] truncate">{c.label}</span>
          <X size={11} className="opacity-60 group-hover:opacity-100" />
        </button>
      ))}
      {chips.length > 1 && (
        <button
          type="button"
          onClick={onClearAll}
          data-testid={`${testidPrefix}-clear-all`}
          className="ml-auto inline-flex items-center gap-1 h-7 px-2.5 text-[11.5px] text-slate-500 hover:text-slate-900 font-semibold transition-colors"
          aria-label="Clear all filters"
        >
          <X size={11} /> Clear all
        </button>
      )}
    </div>
  );
}
